import { useEffect, useState } from "react";
import api from "@/lib/api";
import AdminShell from "@/components/AdminShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, Edit3 } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", threshold: 0, color: "#0F8964", perks: [], sort_order: 0 };

export default function AdminTiers() {
  const [tiers, setTiers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [perksText, setPerksText] = useState("");
  const [editing, setEditing] = useState(null);

  const load = async () => setTiers((await api.get("/admin/tiers")).data);
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setPerksText(""); setOpen(true); };
  const edit = (t) => { setEditing(t.id); setForm(t); setPerksText(t.perks?.join("\n") || ""); setOpen(true); };

  const submit = async () => {
    const payload = {
      ...form,
      threshold: Number(form.threshold),
      sort_order: Number(form.sort_order),
      perks: perksText.split("\n").map((p) => p.trim()).filter(Boolean),
    };
    try {
      if (editing) await api.put(`/admin/tiers/${editing}`, payload);
      else await api.post("/admin/tiers", payload);
      setOpen(false); await load();
      toast.success("Saved");
    } catch { toast.error("Save failed"); }
  };

  const remove = async (id) => { await api.delete(`/admin/tiers/${id}`); await load(); };

  return (
    <AdminShell title="Membership tiers">
      <div className="flex justify-end mb-4">
        <Button data-testid="add-tier-btn" onClick={openNew} className="rounded-full bg-mr-primary hover:bg-[var(--mr-primary-hover)]"><Plus className="h-4 w-4 mr-1" /> New tier</Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {tiers.map((t) => (
          <div key={t.id} className="bg-white border border-mr rounded-2xl p-6">
            <div className="flex items-start justify-between mb-3">
              <span className="h-8 w-8 rounded-full" style={{ background: t.color }} />
              <div className="flex gap-1">
                <Button data-testid={`tier-edit-${t.id}`} variant="ghost" size="icon" onClick={() => edit(t)}><Edit3 className="h-4 w-4 text-mr-primary" /></Button>
                <Button data-testid={`tier-del-${t.id}`} variant="ghost" size="icon" onClick={() => remove(t.id)}><Trash2 className="h-4 w-4 text-mr-accent" /></Button>
              </div>
            </div>
            <h3 className="font-display text-2xl font-semibold text-mr-primary">{t.name}</h3>
            <p className="text-mr-secondary text-sm mt-1">{t.threshold.toLocaleString()}+ pts</p>
            <ul className="mt-4 space-y-1.5">
              {t.perks?.map((p, i) => <li key={i} className="text-sm text-mr-primary">• {p}</li>)}
            </ul>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit tier" : "New tier"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name</Label><Input data-testid="tier-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>Threshold (pts)</Label><Input data-testid="tier-threshold" type="number" value={form.threshold} onChange={(e) => setForm({ ...form, threshold: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Color</Label><Input data-testid="tier-color" type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} /></div>
              <div><Label>Sort order</Label><Input data-testid="tier-sort" type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} /></div>
            </div>
            <div><Label>Perks (one per line)</Label><Textarea data-testid="tier-perks" rows={5} value={perksText} onChange={(e) => setPerksText(e.target.value)} /></div>
          </div>
          <DialogFooter><Button data-testid="tier-save-btn" onClick={submit} className="rounded-full bg-mr-primary hover:bg-[var(--mr-primary-hover)]">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminShell>
  );
}
